/**
 * GSAP Animations
 * 
 * This file contains custom entrance animations using GSAP for WordPress Gutenberg blocks.
 * To apply animations, add the class 'gsap-animate' along with specific animation classes to your blocks.
 * 
 * Animation classes:
 * - fade-in: Simple fade in animation
 * - slide-up: Slides up while fading in
 * - slide-down: Slides down while fading in
 * - slide-left: Slides in from left
 * - slide-right: Slides in from right
 * - zoom-in: Zooms in while fading in
 * - zoom-out: Zooms out while fading in
 * - flip-x: Flips on X axis
 * - flip-y: Flips on Y axis
 * - bounce: Bounces in
 * - text-reveal: Reveals text with a simple animation (works best on headings)
 * - parallax: Applies parallax scrolling effect (works best on wp-block-cover)
 * 
 * Customization classes:
 * - delay-[value]: Add delay in seconds (e.g., delay-0-5, delay-1, delay-2)
 * - duration-[value]: Set animation duration in seconds (e.g., duration-0-5, duration-1, duration-2)
 * - ease-[type]: Set easing type (e.g., ease-power2, ease-back, ease-elastic, ease-bounce)
 * - stagger-[value]: For parent elements with multiple children to animate (e.g., stagger-0-1, stagger-0-2)
 * - parallax-speed-[value]: Set parallax speed (e.g., parallax-speed-2, parallax-speed-5)
 */

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

class GSAPAnimations {
  constructor() {
    this.initialized = false;
    this.scrollTriggers = [];
    this.animationCache = new Map();
    this.intersectionObserver = null;
    this.init();
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.isEditor = this.checkIfEditor();

    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => this.setupAnimationsLazy(), { timeout: 2000 });
    } else {
      setTimeout(() => this.setupAnimationsLazy(), 100);
    }
  }

  checkIfEditor() {
    return !!(
      document.body.classList.contains('wp-admin') || 
      document.body.classList.contains('block-editor-page') ||
      document.querySelector('.edit-post-visual-editor, .block-editor')
    );
  }

  setupAnimationsLazy() {
    if (this.isEditor) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.initializeElementAnimation(entry.target);
            this.intersectionObserver.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px 0px',
        threshold: 0.1
      }
    );

    const animatedElements = document.querySelectorAll('.gsap-animate');
    animatedElements.forEach(element => {
      this.intersectionObserver.observe(element);
    });

    this.setupOptimizedParallax();
  }
  initializeElementAnimation(element) {
    const params = this.getAnimationParams(element);
    let animation;

    if (element.classList.contains('stagger-children')) {
      animation = this.createStaggerAnimation(element, params);
    } else {
      animation = this.createAnimationByType(element, params);
    }

    if (animation) {
      const scrollTrigger = ScrollTrigger.create({
        trigger: element,
        start: "top bottom-=50",
        toggleActions: "play none none none",
        animation: animation,
        once: true,
      });
      
      this.scrollTriggers.push(scrollTrigger);
    }
  }

  createAnimationByType(element, params) {
    const animationMap = {
      'fade-in': () => this.createFadeInAnimation(element, params),
      'slide-up': () => this.createSlideUpAnimation(element, params),
      'slide-down': () => this.createSlideDownAnimation(element, params),
      'slide-left': () => this.createSlideLeftAnimation(element, params),
      'slide-right': () => this.createSlideRightAnimation(element, params),
      'zoom-in': () => this.createZoomInAnimation(element, params),
      'zoom-out': () => this.createZoomOutAnimation(element, params),
      'flip-x': () => this.createFlipXAnimation(element, params),
      'flip-y': () => this.createFlipYAnimation(element, params),
      'bounce': () => this.createBounceAnimation(element, params),
      'text-reveal': () => this.createSimpleTextRevealAnimation(element, params)
    };

    for (const [className, animationFn] of Object.entries(animationMap)) {
      if (element.classList.contains(className)) {
        return animationFn();
      }
    }

    return this.createFadeInAnimation(element, params);
  }
  getAnimationParams(element) {
    // Check cache first
    const cacheKey = element.className;
    if (this.animationCache.has(cacheKey)) {
      return this.animationCache.get(cacheKey);
    }

    const params = this.parseAnimationParams(element);
    this.animationCache.set(cacheKey, params);
    return params;
  }
  parseAnimationParams(element) {
    const classList = Array.from(element.classList); // Convert to array once
    const params = {
      delay: 0,
      duration: 0.8,
      ease: "power2.out",
      stagger: 0.1,
      parallaxSpeed: 0.5,
    };

    const patterns = {
      delay: /^delay-(.+)$/,
      duration: /^duration-(.+)$/,
      ease: /^ease-(.+)$/,
      stagger: /^stagger-(.+)$/,
      parallaxSpeed: /^parallax-speed-(.+)$/
    };

    classList.forEach(className => {
      for (const [param, pattern] of Object.entries(patterns)) {
        const match = className.match(pattern);
        if (match) {
          const valueStr = match[1];
          
          if (param === 'ease') {
            params.ease = this.getEaseValue(valueStr);
          } else {
            const value = valueStr.includes('-') 
              ? parseFloat(valueStr.replace('-', '.')) 
              : parseFloat(valueStr);
            
            if (!isNaN(value)) {
              if (param === 'parallaxSpeed') {
                params[param] = value / 10;
              } else {
                params[param] = value;
              }
            }
          }
          break;
        }
      }
    });

    return params;
  }

  getEaseValue(easeType) {
    const easeMap = {
      'power1': 'power1.out',
      'power2': 'power2.out',
      'power3': 'power3.out',
      'power4': 'power4.out',
      'back': 'back.out(1.7)',
      'elastic': 'elastic.out(1, 0.3)',
      'bounce': 'bounce.out',
      'circ': 'circ.out',
      'expo': 'expo.out',
      'sine': 'sine.out'
    };
    
    return easeMap[easeType] || 'power2.out';
  }

  setupOptimizedParallax() {
    if (this.isEditor) return;

    let ticking = false;
    
    const coverBlocks = document.querySelectorAll('.wp-block-cover.parallax, .wp-block-cover.gsap-parallax');
    
    coverBlocks.forEach(cover => {
      const bgImage = cover.querySelector('.wp-block-cover__background, .wp-block-cover__image-background');
      if (!bgImage) return;
      
      const params = this.getAnimationParams(cover);
      const speed = params.parallaxSpeed;

      gsap.set(cover, { 
        position: 'relative', 
        overflow: 'hidden'
      });
      
      const extraHeight = speed * 100;
      gsap.set(bgImage, { 
        position: 'absolute',
        width: '100%',
        height: `${100 + extraHeight}%`,
        top: `-${extraHeight / 2}%`,
        left: 0,
        willChange: 'transform',
        force3D: true
      });

      const scrollTrigger = ScrollTrigger.create({
        trigger: cover,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1,
        onUpdate: (self) => {
          if (!ticking) {
            requestAnimationFrame(() => {
              gsap.set(bgImage, {
                y: `${(self.progress - 0.5) * extraHeight}%`,
                force3D: true
              });
              ticking = false;
            });
            ticking = true;
          }
        }
      });
      
      this.scrollTriggers.push(scrollTrigger);
    });
  }

  createFadeInAnimation(element, params) {
    return gsap.to(element, {
      opacity: 1,
      duration: params.duration,
      delay: params.delay,
      ease: params.ease,
      force3D: true,
    });
  }

  createSlideUpAnimation(element, params) {
    gsap.set(element, { y: 30, force3D: true }); // Reduced distance for smoother animation
    return gsap.to(element, {
      y: 0,
      opacity: 1,
      duration: params.duration,
      delay: params.delay,
      ease: params.ease,
      force3D: true,
    });
  }

  createSlideDownAnimation(element, params) {
    gsap.set(element, { y: -30, force3D: true });
    return gsap.to(element, {
      y: 0,
      opacity: 1,
      duration: params.duration,
      delay: params.delay,
      ease: params.ease,
      force3D: true,
    });
  }

  createSlideLeftAnimation(element, params) {
    gsap.set(element, { x: 30, force3D: true });
    return gsap.to(element, {
      x: 0,
      opacity: 1,
      duration: params.duration,
      delay: params.delay,
      ease: params.ease,
      force3D: true,
    });
  }

  createSlideRightAnimation(element, params) {
    gsap.set(element, { x: -30, force3D: true });
    return gsap.to(element, {
      x: 0,
      opacity: 1,
      duration: params.duration,
      delay: params.delay,
      ease: params.ease,
      force3D: true,
    });
  }

  createZoomInAnimation(element, params) {
    gsap.set(element, { scale: 0.8, force3D: true }); // Less dramatic scale for smoother animation
    return gsap.to(element, {
      scale: 1,
      opacity: 1,
      duration: params.duration,
      delay: params.delay,
      ease: params.ease,
      force3D: true,
    });
  }

  createZoomOutAnimation(element, params) {
    gsap.set(element, { scale: 1.2, force3D: true });
    return gsap.to(element, {
      scale: 1,
      opacity: 1,
      duration: params.duration,
      delay: params.delay,
      ease: params.ease,
      force3D: true,
    });
  }

  createFlipXAnimation(element, params) {
    gsap.set(element, { rotationX: 45, force3D: true }); // Reduced rotation for smoother animation
    return gsap.to(element, {
      rotationX: 0,
      opacity: 1,
      duration: params.duration,
      delay: params.delay,
      ease: params.ease,
      force3D: true,
    });
  }

  createFlipYAnimation(element, params) {
    gsap.set(element, { rotationY: 45, force3D: true });
    return gsap.to(element, {
      rotationY: 0,
      opacity: 1,
      duration: params.duration,
      delay: params.delay,
      ease: params.ease,
      force3D: true,
    });
  }

  createBounceAnimation(element, params) {
    gsap.set(element, { y: -30, force3D: true });
    return gsap.to(element, {
      y: 0,
      opacity: 1,
      duration: params.duration,
      delay: params.delay,
      ease: "bounce.out",
      force3D: true,
    });
  }

  createSimpleTextRevealAnimation(element, params) {
    gsap.set(element, { 
      opacity: 1,
      clipPath: 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)',
      force3D: true
    });
    
    return gsap.to(element, {
      clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
      duration: params.duration,
      delay: params.delay,
      ease: params.ease,
      force3D: true,
    });
  }

  createStaggerAnimation(element, params) {
    let children = Array.from(element.children);
    
    if (element.classList.contains('jet-listing-grid--blocks')) {
      const gridItems = element.querySelectorAll('.jet-listing-grid__item');
      if (gridItems.length > 0) {
        children = Array.from(gridItems);
      }
    }
    
    if (children.length === 0) return null;

    gsap.set(element, { opacity: 1 });
    gsap.set(children, { y: 20, force3D: true });
    
    return gsap.to(children, {
      opacity: 1,
      y: 0,
      duration: params.duration,
      delay: params.delay,
      stagger: params.stagger,
      ease: params.ease,
      force3D: true,
    });
  }

  destroy() {
    this.scrollTriggers.forEach(trigger => trigger.kill());
    this.scrollTriggers = [];
    
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    
    this.animationCache.clear();
    
    this.initialized = false;
  }
}

export default GSAPAnimations;
